// React imports
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane, faSave, faExternalLink, faExclamationTriangle, faCheck, faXmark, faWarning, faPencil, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// Local imports
import email from '../utils/email';
import OpportunityOneOffFreeTextBuilder from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.js';
import OpportunityOneOffBuilder from '../opportunities/OpportunityOneOffBuilder.js';
import OpportunityOneOffAccountRouteProductsSelector from '../opportunities/OpportunityOneOffAccountRouteProductsSelector.js';
import AttachmentWarning from './EmailPage.attachmentwarning.js';
import { getErrors, minimizeProducts } from '../utils/opportunity.js';
import { offerToText } from '../opportunities/OpportunityOneOffEmailBuilder.utils.js';
import { allFieldsAssignedToSections } from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.utils.js';
import ContextMenu from '../components/ContextMenu.js';
import Reply from './EmailPage.reply.js';
import getBaseUrl from '../../GetBaseUrl.js';
import SidePaneStructure from '../sidepane/SidePane.structure.js';
import { getDefaultEmailPrefaceForEmailPage, getDefaultEmailBody } from '../opportunities/OpportunityOneOffEmailBuilder.utils.js';
import flattenThread from '../utils/flattenThread.js';

function getDefaultPreface(org, link) {
    return getDefaultEmailPrefaceForEmailPage({ organization: org, link });
}

function OpportunityEditor(props) {
    const { onClose, organization, accesstoken, conversation, conversationId, globalMessageId, exchangeRates, lockedExchangeRates,
        me, emailLoginInfo, accounts, suppliers, seaPorts, unlocode, tokens, onUpdateOpportunity, setAccounts, opportunity, 
        opportunities, allOpportunities, scrollToAdding,
     } = props;

    // Host a pasted screenshot rather than leaving it inline: base64 images do not render
    // in mail clients, and the quote this becomes is stored as a single JSON blob.
    async function onUploadImage({ dataUrl, contentType }) {
        const { data } = await axios.post(
            `/api/users/signature-image?accesstoken=${accesstoken}&organizationId=${organization.id}`,
            { image: dataUrl, contentType, prefix: 'quote-images' }
        );
        return data?.url;
    }

    const [ showAttachmentWarning, setShowAttachmentWarning ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const [ savingNewWindow, setSavingNewWindow ] = useState(false);
    const [ savingPreview, setSavingPreview ] = useState(false);
    const [ saveError, setSaveError ] = useState(null);
    const [ saveErrorNonce, setSaveErrorNonce ] = useState(0); // bumped on each blocked save so the scroll re-fires even if the error is unchanged
    const [ savingSuccess, setSavingSuccess ] = useState(false);
    const [ sending, setSending ] = useState(false);
    const [ sendError, setSendError ] = useState(null);
    const [ sendingSuccess, setSendingSuccess ] = useState(false);
    const [ to, setTo ] = useState([]);
    const [ cc, setCC ] = useState([]);
    const [ subject, setSubject ] = useState('');
    const [ allRecipients, setAllRecipients ] = useState([]);
    const [ useAdvanedWarning, setUseAdvanedWarning ] = useState(false);
    const [ forceOpen, setForceOpen ] = useState(null);
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const id = useMemo(() => { return opportunity?.id || uuidv4(); }, [opportunity]);

    const attachPDF = opportunity?.attributes?.attachPDF || false;
    const [ includeThread, setIncludeThread ] = useState(_.get(organization, 'integrations.email.includeOriginalThread', true));

    function _setAttachPDF(newAttachPDF) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, attributes: { ...(op.attributes || {}), attachPDF: newAttachPDF } }) });
    }

    useEffect(() => {
        if (!opportunity) {
            const defaultViewTemplate = organization?.defaults?.viewTemplate || 'standard';
            const defaultPreface = getDefaultPreface(organization, 'https://PLACEHOLDER_FOR_LINK');
            onUpdateOpportunity({
                opportunityId: id, func: opportunity => ({
                    ...opportunity,
                    type: 'one-off-free-text',
                    id,
                    status: 'pre-draft',
                    source: { type: 'email', id: conversationId, globalMessageId },
                    owner: me.email,
                    offers: [],
                    preface: defaultPreface,
                    ...(defaultViewTemplate !== 'standard' ? { attributes: { viewTemplate: defaultViewTemplate } } : {})
                })
            });
        }
    }, [!opportunity, conversationId, globalMessageId])

    // Initialize viewTemplate from org defaults when opportunity is first created
    useEffect(() => {
        if (opportunity && id && (opportunity.status === 'draft' || opportunity.status === 'pre-draft' || !opportunity.status) && !opportunity.attributes?.viewTemplate) {
            const defaultViewTemplate = organization?.defaults?.viewTemplate || 'standard';
            if (defaultViewTemplate !== 'standard') {
                onUpdateOpportunity({ 
                    opportunityId: id, 
                    func: opportunity => ({ 
                        ...opportunity, 
                        attributes: { 
                            ...(opportunity.attributes || {}), 
                            viewTemplate: defaultViewTemplate 
                        } 
                    }) 
                });
            }
        }
    }, [opportunity?.id, opportunity?.status, opportunity?.attributes?.viewTemplate, organization?.defaults?.viewTemplate, id])

    const type = opportunity?.type || 'one-off-free-text';
    function _setType(newType) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, type: newType }) });
    }

    const status = opportunity?.status;
    function _setStatus(newStatus) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, status: newStatus }) });
    }

    const offers = opportunity?.offers || [];
    // True once the user has entered any cost (a field with a value/formula) on the
    // mailbox quote. Switching to the Form Builder overwrites `offers` with [] (see the
    // "Use Form Builder" handler below), so these costs don't carry over — we use this to
    // warn the user their work might be lost before they switch.
    const hasEnteredCosts = (offers || []).some(o =>
        (o.sections || []).some(s =>
            (s.offers || []).some(so =>
                (so.fields || []).some(f =>
                    _.values(f.values || {}).some(v => v && (v.value != null || (typeof v.formula === 'string' && v.formula.trim() !== '')))
                )
            )
        )
    );
    function _setOffers(newOffers) {
        onUpdateOpportunity({ opportunityId: id, func: opportunity => ({ ...opportunity, offers: _.isFunction(newOffers) ? newOffers(opportunity.offers) : newOffers }) });
    }

    const preface = opportunity?.preface || '';
    function _setPreface(newPreface) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, preface: newPreface }) });
    }

    const incoterm = opportunity?.incoterm || '';
    function _setIncoterm(newIncoterm) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, incoterm: newIncoterm }) });
    }

    const terms = opportunity?.terms || [];
    function _setTerms(newTerms) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, terms: newTerms }) });
    }

    const routes = opportunity?.routes || [];
    function _setRoutes(newRoutes) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, routes: newRoutes }) });
    }

    const transportationMethod = opportunity?.transportationMethod || null;
    function _setTransportationMethod(newTransportationMethod) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, transportationMethod: newTransportationMethod }) });
    }

    const buyingRates = opportunity?.buyingRates || [];
    function _setBuyingRates(newBuyingRates) {
        const sanitize = br => ({ ..._.omit(br, '_rfqSource'), product: minimizeProducts({ products: [br.product] })[0] });
        if (_.isFunction(newBuyingRates)) {
            onUpdateOpportunity({ opportunityId: id, func: opportunity => ({ ...opportunity, buyingRates: newBuyingRates(opportunity.buyingRates).map(sanitize) }) });
        } else {
            onUpdateOpportunity({ opportunityId: id, func: opportunity => ({ ...opportunity, buyingRates: newBuyingRates.map(sanitize) }) });
        }
    }

    const updates = opportunity?.updates || [];
    function _setUpdates({ updates: newUpdates }) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, updates: newUpdates }) });
    }

    const description = opportunity?.description || {};
    function _setDescription(newDescription) {
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, description: newDescription }) });
    }

    const account = opportunity?.account || {};
    function _setAccount(newAccount) {
        if (!newAccount.id) {
            newAccount.id = uuidv4();
            newAccount.status = 'draft';
            setAccounts(acc => [...acc, newAccount]);
        }
        onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, account: newAccount }) });
    }

    const products = opportunity?.products || [];
    function _setProducts(newProducts) {
        if (typeof newProducts === 'function') {
            onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, products: newProducts(op.products || []) }) });
        } else {
            onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, products: newProducts }) });
        }
    }

    useEffect(() => {
        return () => {
            console.log('[OpportunityEditor] unmounted');
            window.searchPaneLoadFromOpportunity = null;
            window.onUpdateSearchPaneLoadFromOpportunity?.forEach(fn => fn(window.searchPaneLoadFromOpportunity));
        }
    }, []);

    useEffect(() => {
        window.searchPaneLoadFromOpportunity = {
            opportunityId: id,
            routes,
            transportationMethod,
            products,
            source: { type: 'email', id: conversationId, globalMessageId },
        };
        window.onUpdateSearchPaneLoadFromOpportunity?.forEach(fn => fn(window.searchPaneLoadFromOpportunity));
    }, [opportunity]);

    async function _onClose({ sent = false } = {}) {
        if (sent) {
            onClose({ sent });
            return;
        }

        // Ask for confirmation before deleting
        setShowDeleteConfirm(true);
    }

    async function handleDeleteConfirm() {
        setShowDeleteConfirm(false);

        if (!opportunity) {
            onClose({ sent: false });
            return;
        }

        const currentStatus = opportunity?.status || 'pre-draft';

        if (currentStatus === 'draft' || currentStatus === 'pre-draft') {
            // For draft/pre-draft, try to delete via API if it exists on server
            // Otherwise, remove it from the local list
            try {
                await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                    opportunity: {
                        status: 'deleted'
                    }
                });
                // Update local state to remove from list
                onUpdateOpportunity({ opportunityId: id, func: () => null });
            } catch (e) {
                // If API call fails (e.g., opportunity doesn't exist on server yet), 
                // just remove it from local list
                console.log('Opportunity not found on server, removing locally:', e);
                onUpdateOpportunity({ opportunityId: id, func: () => null });
            }
            onClose({ sent: false });
        } else {
            // For non-draft opportunities, set status to 'deleted'
            try {
                await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                    opportunity: {
                        status: 'deleted'
                    }
                });
                onUpdateOpportunity({ opportunityId: id, func: (op) => ({ ...op, status: 'deleted' }) });
                onClose({ sent: false });
            } catch (e) {
                console.error('Error deleting opportunity:', e);
                setSaveError('Failed to delete opportunity');
                await new Promise(res => setTimeout(res, 3000));
                setSaveError(null);
            }
        }
    }

    function handleDeleteCancel() {
        setShowDeleteConfirm(false);
    }

    useEffect(() => {
        const sorted = _.sortBy(conversation.filter(c => c.sender?.emailAddress?.address !== emailLoginInfo.email), a => dayjs(a.receivedDateTime).valueOf())
        const _to = [(_.last(sorted) || _.last(conversation))?.sender?.emailAddress];
        const _cc = [ 
            ...((_.last(sorted) || _.last(conversation))?.toRecipients || []).map(r => r?.emailAddress),
            ...((_.last(sorted) || _.last(conversation))?.ccRecipients || []).map(r => r?.emailAddress),
        ].filter(r => r?.address !== emailLoginInfo.email);

        if (!opportunity || !opportunity.attributes?.email) {
            setTo(_.compact(_to));
            setCC(_.compact(_cc));
            setSubject((_.last(sorted)?.subject || '').startsWith('Re:') ? _.last(sorted)?.subject || '' : `Re: ${_.last(sorted)?.subject || ''}`);
        } else if (opportunity?.attributes?.email) {
            setTo(_.compact(opportunity.attributes.email.to));
            setCC(_.compact(opportunity.attributes.email.cc));
            setSubject(opportunity.attributes.email.subject);
        }
        setAllRecipients(_.uniqBy(_.compact([
            ..._to,
            ..._cc,
            ...(organization.users.map(u => ({ address: u.email, name: u.name }))),
            ...(accounts.map(a => (a.contacts || []).map(c => ({ address: c.email, name: `${c.firstName ? `${c.firstName} ` : ''}${c.lastName ? `${c.lastName}` : ''}${c.firstName || c.lastName ? ' at ' : 'Contact at '}${a.name ? `${a.name}` : ''}` })))).flat(),
        ]), a => a.address));
    }, [conversation])

    useEffect(() => {
        if (status === 'pre-draft') {
            scrollToAdding();
        }
    }, [conversation, status]);

    const ERROR_FIX_MAP = {
        'account:id': () => setForceOpen('account'),
        'incoterm:transportationMethod': () => setForceOpen('transportationMethod'),
        'incoterm:incoterm': () => setForceOpen('transportationMethod'),
        'builder:0:offertitle': () => setForceOpen('builder:0:offertitle'),
        'builder:1:offertitle': () => setForceOpen('builder:1:offertitle'),
        'builder:2:offertitle': () => setForceOpen('builder:2:offertitle'),
        'builder:3:offertitle': () => setForceOpen('builder:3:offertitle'),
        'builder:4:offertitle': () => setForceOpen('builder:4:offertitle'),
        'builder:5:offertitle': () => setForceOpen('builder:5:offertitle'),
    }

    function getOpportunityToSave() {
        const newOffers = _.cloneDeep(offers);
        newOffers.forEach((offer, oi) => {
            if (offer.displayCurrency?.convert) {
                offer.displayCurrencySource = offer.displayCurrency.source;
                offer.displayCurrency = offer.displayCurrency.to;
            } else if (offer.displayCurrency?.convert === false) {
                delete offer.displayCurrency;
                delete offer.displayCurrencySource;
            }
            offer.sections.forEach((section, si) => {
                if (section.displayCurrency?.convert) {
                    section.displayCurrencySource = section.displayCurrency.source;
                    section.displayCurrency = section.displayCurrency.to;
                } else if (section.displayCurrency?.convert === false) {
                    delete section.displayCurrency;
                    delete section.displayCurrencySource;
                }
                section.offers.forEach((sectionOffer, soi) => sectionOffer.fields.forEach((field, fi) => {
                    if (field.displayCurrency?.convert) {
                        field.displayCurrencySource = field.displayCurrency.source;
                        field.displayCurrency = field.displayCurrency.to;
                    } else if (field.displayCurrency?.convert === false) {
                        delete field.displayCurrency;
                        delete field.displayCurrencySource;
                    }
                    if (field.type === 'per-unit') {
                        field.values = _.pick(field.values, [...products.map(p => p.id), 'custom', 'flat']);
                        const productValue = _.first(_.values(_.pick(field.values, products.map(p => p.id))));

                        if (productValue) {
                            products.forEach((product, pi) => {
                                field.values[product.id] = productValue;
                            });
                        }
                    }
                }));
            });
        });

        const _opportunity = {
            ..._.omit(opportunity, ['id', 'organizationId', 'createdBy', 'owner', 'modified', 'log', 'created']),
            status: status === 'draft' ? null : status || null,
            type,
            account,
            tags: [],
            incoterm,
            terms,
            products: minimizeProducts({ products }),
            offers: newOffers,
            transportationMethod,
            routes,
            preface,
            epilogue: '',
            source: { type: 'email', id: conversationId, globalMessageId },
            buyingRates: (buyingRates || []).map(br => ({ ...br, product: minimizeProducts({ products: [br.product] })[0] })),
            exchangeRates: lockedExchangeRates,
            attributes: { ...opportunity?.attributes || {}, attachPDF },
        };

        return _opportunity;
    }

    async function onSaveDraft({ quiet = false } = {}) {

        const _opportunity = getOpportunityToSave();
        const er = getErrors({ opportunity: _opportunity });
        const allErrors = _.values({ ...er, header: [] }).flat();

        if (!_.isEmpty(allErrors)) {
            setSaveError('opportunity-errors');
            setSaveErrorNonce(n => n + 1);
            return;
        }

        setSaveError(null);

        if (!quiet) setSaving(true);

        try {
            if (account.status === 'draft') {
                const { data: newAccount } = await axios.put(`/api/accounts/${account.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                    account: {
                        name: account.name,
                        tags: [{ label: 'Pipeline: New' }],
                    }
                });
                setAccounts(acc => [...acc.filter(a => a.id !== account.id), newAccount ]);
                _setAccount(_.pick(newAccount, ['id', 'name', 'tags']));
            }

            const { data: __opportunity } = await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                opportunity: {
                    ..._opportunity,
                    account: _.pick(account, ['id', 'email']),
                    tags: [],
                    attributes: { ...opportunity?.attributes || {}, 'email': { to, cc, subject } }
                }
            });

            onUpdateOpportunity({ opportunity: __opportunity });
            if (!quiet) {
                setSavingSuccess(true);
                await new Promise(res => setTimeout(res, 3000));
                setSavingSuccess(false);
                setSaving(false);
            }
            return true;
        } catch (e) {
            setSaving(false);
            console.error(e);
            if (e.response) {
                setSaveError(e.response.data.detail || e.response.data.title || e.response.data);
            } else {
                setSaveError(e.toString());
            }
            return false;
        }
    }

    async function onSend() {

        if (!preface) {
            setSendError('Please add a body to your email.');
            await new Promise(res => setTimeout(res, 3000));
            setSendError(null);
            return;
        }

        if (_.isEmpty(offers)) {
            setSendError('Please add at least one offer to your quote.');
            await new Promise(res => setTimeout(res, 3000));
            setSendError(null);
            return;
        }

        setSending(true);

        if (!await onSaveDraft()) {
            setSending(false);
            return;
        };

        try {
            const { data: link } = await axios(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&type=end-customer&organizationId=${organization.id}&minimized=false`)

            const terms = opportunity.terms;
            const _terms = !_.isEmpty(terms) ? terms : _.get(organization, `terms['price-quote-${transportationMethod}']`, null) || _.get(organization, 'terms[\'price-quote\']', null);
            const __terms = !!_terms && _.isString(_terms) ? [{ value: _terms }] : _terms;

            let body = offerToText({
                type: opportunity.type,
                offers: opportunity.offers,
                preface: opportunity.preface,
                epilogue: '', // `Click <a href="https://app.monada.ai/offers?temporaryAccesstoken=${link}">here</a> to accept this quote, and we'll get started on moving your cargo!<br/><br/>` + _opportunity.epilogue,
                products: opportunity.products,
                transportationMethod: opportunity.transportationMethod,
                exchangeRates,
                terms: __terms,
                buyingRates: opportunity.buyingRates,
                lockedExchangeRates,
                organization
            });
            body = body.replace('PLACEHOLDER_FOR_LINK', `app.monada.ai/offers?temporaryAccesstoken=${link}`);

            // Ensure Poppins font is included if not already present
            if (!body.includes('font-family') && !body.includes('fontFamily')) {
                body = `<div style="font-family: 'Poppins', sans-serif;">${body}</div>`;
            }

            const data = await email.sendReply({
                org: organization,
                tokens,
                conversationId: _.last(_.compact(conversation.map(c => c.conversationId))),
                toRecipients: to,
                ccRecipients: cc,
                subject,
                body,
                attachments: [],
                monadaAccesstoken: accesstoken,
                monadaOrganizationId: organization.id,
                useQueue: true,
                generatePdf: attachPDF ? { opportunityId: id, minimized: false } : null,
                opportunityId: id,
                includeThread,
            });

            // The email is only QUEUED here, not yet sent. Mark the quote as
            // 'sending-to-endcustomer' (shows "Sending...") until the worker confirms
            // delivery. Marking it 'sent-to-endcustomer' prematurely caused the
            // worker's PDF-generation render to flip the quote to 'opened-by-customer'
            // while the email was still sending. The worker (and the poll below) move
            // it to 'sent-to-endcustomer' once the email is actually sent.
            const { data: _opportunity } = await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                opportunity: {
                    status: 'sending-to-endcustomer',
                    pendingEmailId: data.id,
                },
                reason: JSON.stringify({ source: 'email-queued', toRecipients: to, ccRecipients: cc, emailId: data.id })
            });

            setSending(false);
            setSendingSuccess(true);
            onUpdateOpportunity({ opportunity: _opportunity });

            // Poll in the background and flip to 'sent-to-endcustomer' once the email
            // has actually been sent, so the UI reflects delivery without a refresh.
            (async () => {
                try {
                    const pollResult = await email.waitForEmailSent({ org: organization, emailId: data.id, monadaAccesstoken: accesstoken });
                    if (pollResult?.success) {
                        const { data: sentOpportunity } = await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                            opportunity: {
                                status: 'sent-to-endcustomer',
                                pendingEmailId: null,
                            },
                            reason: JSON.stringify({ source: 'email-sent', toRecipients: to, ccRecipients: cc, emailId: data.id })
                        });
                        onUpdateOpportunity({ opportunity: sentOpportunity });
                    }
                } catch (pollError) {
                    console.error('Failed to confirm quote send status', pollError);
                }
            })();
            onClose({ sent: true, id: data.id, queuedEmailId: data.id, pendingMessage: {
                id: data.id,
                subject,
                body,
                toRecipients: to,
                ccRecipients: cc,
                status: 'pending'
            }});

        } catch (e) {
            setSending(false);
            console.error(e);
            if (e.response) {
                setSendError(e.response.data.detail || e.response.data.title || e.response.data || 'An error occurred while sending the email.');
            } else {
                setSendError(e.toString() ? e.toString() : 'An error occurred while sending the email.');
            }

            await new Promise(res => setTimeout(res, 3000));
            setSendError(null);
            return false;
        }
    }

    async function onOpenFullScreen() {
        setSavingNewWindow(true);
        await onSaveDraft({ quiet: true });
        setSavingNewWindow(false);
        navigate(`/organization/${organization.alias}/opportunities/${id}/`);
    }

    async function onCreateOpportunity({ attachments = [] }) {
        _setUpdates([{
            created: new Date().getTime(),
            createdBy: me.email,
            files: JSON.parse(JSON.stringify(attachments)),
            html: `
                    <div style="font-size: 11px;">
                        <p>We'll automatically fill out the opportunity information based on the email you provided.</p>
                    </div>`,
        }]);
        setShowAttachmentWarning(false);
        scrollToAdding();
    }

    const errorAlertRef = useRef(null);

    // A blocked save renders its validation list near the bottom of the composer — out of view
    // when the user clicks the save icon at the top, so it looks like "nothing happened". Scroll
    // the error block into view whenever a save error appears so the user sees why it didn't save.
    useEffect(() => {
        if (saveError && errorAlertRef.current) {
            errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [saveError, saveErrorNonce]);

    async function onPreview() {
        setSavingPreview(true);
        if (!await onSaveDraft({ quiet: true })) {
            setSavingPreview(false);
            return;
        };
        const { data: preview } = await axios(`/api/opportunities/${id}/offer-url?accesstoken=${accesstoken}&organizationId=${organization.id}&type=preview&minimized=false`);
        setSavingPreview(false);
        window.open(`${getBaseUrl()}/offers?temporaryAccesstoken=${preview}&type=preview`, '_blank');
    }

    if (showAttachmentWarning) {
        return (
            <AttachmentWarning
                open={!!showAttachmentWarning}
                conversationId={conversationId}
                conversations={[conversation]}
                onClose={() => {
                    setShowAttachmentWarning(null);
                    scrollToAdding();
                }}
                onCreateOpportunity={onCreateOpportunity}
                org={organization}
                tokens={tokens}
                accesstoken={accesstoken}
            />
        )
    }
    
    if (!opportunity) return null;

    // Compute errors for preview button
    const _opportunityForErrors = getOpportunityToSave();
    const er = getErrors({ opportunity: _opportunityForErrors });
    const allErrors = _.values({ ..._.mapValues(er, (e,k) => e.map(ee => ({ ...ee, id: `${k}:${ee.id}` }))), header: [] }).flat();
    const hasErrors = !_.isEmpty(allErrors);

    let errorDescription = null;
    if (saveError === 'opportunity-errors') {
        if (hasErrors) {
            errorDescription = (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: '#f0f9ff',
                    borderLeft: '4px solid #0284c7',
                    borderRadius: '0 8px 8px 0',
                    padding: '12px 16px',
                    gap: '8px'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: '16px', color: '#0284c7' }} />
                        <Typography variant='body2' sx={{ fontSize: '12px', fontWeight: 500, color: '#0c4a6e' }}>
                            Just a few things to finalize:
                        </Typography>
                    </Box>
                    {allErrors.map(e => (
                        <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '24px' }}>
                            <Typography variant='caption' sx={{ fontSize: '11px', color: '#0c4a6e' }}>
                                {e.message}
                            </Typography>
                            {ERROR_FIX_MAP[e.id] && (
                                <Button 
                                    variant='text' 
                                    size='small' 
                                    sx={{ 
                                        m: 0, 
                                        p: 0, 
                                        textTransform: 'none', 
                                        fontSize: '11px',
                                        color: '#3b82f6',
                                        textDecoration: 'underline',
                                        minWidth: 'auto',
                                        '&:hover': {
                                            color: '#2563eb',
                                            backgroundColor: 'transparent',
                                            textDecoration: 'underline'
                                        }
                                    }} 
                                    onClick={() => ERROR_FIX_MAP[e.id]()}
                                >
                                    Fix
                                </Button>
                            )}
                        </Box>
                    ))}
                </Box>
            );
        }
    } else if (saveError) {
        errorDescription = saveError;
    }

    return (
        <Box sx={{ background: 'white', borderRadius: 2, mb: 2, border: '1px solid #aaa', position: 'relative' }}>
            <Box sx={{ p: 2, display: 'flex' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiChip-label': {
                        fontSize: '11px',
                        fontFamily: 'Poppins, sans-serif',
                    },
                    '& .MuiAutocomplete-option': {
                        fontSize: '11px',
                        fontFamily: 'Poppins, sans-serif',
                    },
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography variant='caption' sx={{ width: '60px' }}>To:</Typography>
                        <Autocomplete
                            fullWidth
                            options={allRecipients.filter(r => !([...(to || []), ...(cc || [])].map(a => a.address).includes(r.address)))}
                            filterOptions={(options, state) => {
                                return options
                                  .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                  )
                                  .slice(0, 100)
                              }
                            }
                            multiple
                            freeSolo
                            value={to || []}
                            onChange={(_, value) => setTo(value)}
                            onBlur={(e) => {
                                e.target.dispatchEvent(
                                    new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
                                );
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.name ? `${option.name} <${option.address}>` : option.address;
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            slotProps={{
                                paper: {
                                    sx: { border: '1px solid #aaa', boxShadow: 'none' }
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                        <Typography variant='caption' sx={{ width: '60px' }}>Cc:</Typography>
                        <Autocomplete
                            fullWidth
                            options={allRecipients.filter(r => !([...(to || []), ...(cc || [])].map(a => a.address).includes(r.address)))}
                            filterOptions={(options, state) => {
                                return options
                                  .filter((o) =>
                                    o.name?.toLowerCase().includes(state.inputValue.toLowerCase()) || o.address?.toLowerCase().includes(state.inputValue.toLowerCase())
                                  )
                                  .slice(0, 100)
                              }
                            }
                            multiple
                            freeSolo
                            value={cc || []}
                            onChange={(_, value) => setCC(value)}
                            onBlur={(e) => {
                                e.target.dispatchEvent(
                                    new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
                                );
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.name ? `${option.name} <${option.address}>` : option.address;
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            slotProps={{
                                paper: {
                                    sx: { border: '1px solid #aaa', boxShadow: 'none' }
                                }
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title='Save draft' arrow>
                        <span>
                            <IconButton
                                onClick={!savingSuccess ? onSaveDraft : null}
                                disabled={(saving || savingNewWindow || savingPreview) && !savingSuccess}
                                color={savingSuccess ? 'success' : 'default'}
                            >
                                {savingSuccess ?
                                    <FontAwesomeIcon icon={faCheck} />
                                : saving ?
                                    <CircularProgress size={16} sx={{ color: grey[400] }} />
                                :
                                    <FontAwesomeIcon icon={faSave} />
                                }
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton onClick={_onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', p: 2, pt: 0 }}>
                <Typography variant='caption' sx={{ width: '60px' }}>Subject:</Typography>
                <TextField
                    fullWidth
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                />
            </Box>
            {type === 'one-off-free-text' && (
                <>
                    <OpportunityOneOffAccountRouteProductsSelector
                        org={organization}
                        me={me}
                        account={account}
                        setAccount={_setAccount}
                        routes={routes}
                        setRoutes={_setRoutes}
                        incoterm={incoterm}
                        setIncoterm={_setIncoterm}
                        products={products}
                        setProducts={_setProducts}
                        seaPorts={seaPorts}
                        description={description}
                        setDescription={_setDescription}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={_setTransportationMethod}
                        unlocode={unlocode}
                        accesstoken={accesstoken}
                        accounts={accounts}
                        source={{ type: 'email', id: conversationId, globalMessageId: globalMessageId || '' }}
                        status={status}
                        setStatus={_setStatus}
                        updates={updates}
                        setUpdates={_setUpdates}
                        emailLoginInfo={emailLoginInfo}
                        forceOpen={forceOpen}
                        setForceOpen={setForceOpen}
                    />
                    <OpportunityOneOffFreeTextBuilder
                        opportunityId={id}
                        opportunity={opportunity}
                        org={organization}
                        accesstoken={accesstoken}
                        conversationId={conversationId}
                        transportationMethod={transportationMethod}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                        offers={offers}
                        setOffers={_setOffers}
                        preface={preface}
                        setPreface={_setPreface}
                        products={products}
                        setProducts={_setProducts}
                        setTransportationMethod={_setTransportationMethod}
                        setRoutes={_setRoutes}
                        suppliers={suppliers}
                        buyingRates={buyingRates}
                        setBuyingRates={_setBuyingRates}
                        incoterm={incoterm}
                        routes={routes}
                        terms={terms}
                        setTerms={_setTerms}
                        opportunities={allOpportunities}
                        accounts={accounts}
                        onSave={onSaveDraft}
                        attachPDF={attachPDF}
                        setAttachPDF={_setAttachPDF}
                        onOpenFullScreen={onOpenFullScreen}
                        onFormBuilder={type === 'one-off' ? null : () => setUseAdvanedWarning(true)}
                        isOwner={opportunity?.owner === me?.email}
                    />
                </>
            )}
            {type === 'one-off' && (
                <Box sx={{ mb: 0 }}>
                    <OpportunityOneOffAccountRouteProductsSelector
                        org={organization}
                        me={me}
                        account={account}
                        setAccount={_setAccount}
                        routes={routes}
                        setRoutes={_setRoutes}
                        incoterm={incoterm}
                        setIncoterm={_setIncoterm}
                        products={products}
                        setProducts={_setProducts}
                        seaPorts={seaPorts}
                        description={description}
                        setDescription={_setDescription}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={_setTransportationMethod}
                        unlocode={unlocode}
                        accesstoken={accesstoken}
                        accounts={accounts}
                        source={{ type: 'email', id: conversationId, globalMessageId: globalMessageId || '' }}
                        status={status}
                        setStatus={_setStatus}
                        updates={updates}
                        setUpdates={_setUpdates}
                        emailLoginInfo={emailLoginInfo}
                        forceOpen={forceOpen}
                        setForceOpen={setForceOpen}
                    />
                    <Paper sx={{ backgroundColor: '#FFFFFF', borderRadius: '16px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', mb: 3, overflow: 'hidden' }}>
                        <Box sx={{ background: 'transparent', padding: '6px 24px', paddingTop: '24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                                Compose Email
                            </Typography>
                        </Box>
                        <Box sx={{ padding: '0px 0px 24px 0px' }}>
                            <Reply
                                key={`reply-${type}-${opportunity?.id || id}`}
                                onBlur={({ html }) => _setPreface(html)}
                                initialContent={opportunity?.preface}
                                onUploadImage={onUploadImage}
                            />
                        </Box>
                    </Paper>
                    <OpportunityOneOffBuilder
                        opportunityId={id}
                        org={organization}
                        me={me}
                        accesstoken={accesstoken}
                        conversationId={conversationId}
                        transportationMethod={transportationMethod}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                        offers={offers}
                        setOffers={_setOffers}
                        preface={preface}
                        setPreface={_setPreface}
                        products={products}
                        suppliers={suppliers}
                        buyingRates={buyingRates}
                        setBuyingRates={_setBuyingRates}
                        incoterm={incoterm}
                        routes={routes}
                        terms={terms}
                        setTerms={_setTerms}
                        opportunities={opportunities}
                        owner={opportunity?.owner}
                        errors={[]}
                        forceOpen={forceOpen}
                        setForceOpen={setForceOpen}
                        onPreview={onPreview}
                        validationErrors={hasErrors ? allErrors : []}
                        errorFixMap={ERROR_FIX_MAP}
                    />
                </Box>
            )}

            {errorDescription && (
                <Box ref={errorAlertRef} sx={{ mb: 1 }}>
                    {errorDescription}
                </Box>
            )}

            <Box sx={{ px: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeThread}
                            onChange={e => setIncludeThread(e.target.checked)}
                            color='primary'
                            size='small'
                        />
                    }
                    label={<Typography variant='body2'>Include original thread</Typography>}
                />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', p: 2 }}>
                <Button
                    variant='contained'
                    color={sendError ? 'error' : 'primary'}
                    onClick={sendError ? null : onSend}
                    sx={{ flex: 1 }}
                    disabled={saving || sending || savingNewWindow || savingPreview}
                >
                    {sendingSuccess ?
                        <FontAwesomeIcon icon={faCheck} />
                    : sendError ?
                        <Tooltip title={sendError} open={true}>
                            <FontAwesomeIcon icon={faXmark} />
                        </Tooltip>
                    : sending ?
                        <CircularProgress size={12} sx={{ color: grey[400] }} />
                    :
                        <FontAwesomeIcon icon={faPaperPlane} />
                    }
                    &nbsp;&nbsp;Send
                </Button>
                <Button 
                    variant='outlined' 
                    sx={{ whiteSpace: 'nowrap' }} 
                    onClick={!savingSuccess ? onSaveDraft : null} 
                    disabled={(saving || savingNewWindow || savingPreview) && !savingSuccess}
                    color={savingSuccess ? 'success' : 'primary'}
                >
                    {savingSuccess ?
                        <FontAwesomeIcon icon={faCheck} />
                    : saving ?
                        <CircularProgress size={12} sx={{ color: grey[400] }} />
                    :
                        <FontAwesomeIcon icon={faSave} />
                    }
                    &nbsp;&nbsp;Save draft
                </Button>
                <ContextMenu
                    anchor='right'
                    options={[
                        {
                            key: 2,
                            label: 'Open Fullscreen',
                            onClick: onOpenFullScreen,
                            icon: <FontAwesomeIcon icon={faExternalLink} />,
                            disabled: saving || savingNewWindow || savingPreview
                        },
                        {
                            key: 3,
                            label: 'Form Builder',
                            onClick: () => setUseAdvanedWarning(true),
                            icon: <FontAwesomeIcon icon={faWpforms} />,
                            disabled: type === 'one-off' || opportunity.owner !== me.email
                        },
                    ]}
                />
            </Box>
            <Dialog
                open={useAdvanedWarning}
                onClose={() => setUseAdvanedWarning(false)}
            >
                <DialogTitle>
                    Form Builder
                </DialogTitle>
                <DialogContent>
                <Alert severity='warning' sx={{ mb: 1 }}>
                    <Typography variant='body1'>
                        Using the form builder will overwrite the current opportunity.
                    </Typography>
                </Alert>
                {hasEnteredCosts && (
                    <Alert severity='error' sx={{ mb: 1 }}>
                        <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                            Your work might be lost.
                        </Typography>
                        <Typography variant='body2'>
                            The costs you've already entered on this quote won't carry over to the Form Builder — switching now starts a blank Form Builder quote and these costs will be lost.
                        </Typography>
                    </Alert>
                )}
                {(() => {
                    const missing = [];
                    if (!account?.id) missing.push({ label: 'Account', action: () => { setUseAdvanedWarning(false); setForceOpen('account'); } });
                    if (!transportationMethod) missing.push({ label: 'Transportation method', action: () => { setUseAdvanedWarning(false); setForceOpen('transportationMethod'); } });
                    if (!incoterm) missing.push({ label: 'Incoterm', action: () => { setUseAdvanedWarning(false); setForceOpen('incoterm'); } });
                    if (!products || products.length === 0) missing.push({ label: 'Products', action: () => { setUseAdvanedWarning(false); setForceOpen('products'); } });
                    if (!routes || routes.length === 0) missing.push({ label: 'Route', action: () => { setUseAdvanedWarning(false); setForceOpen('routes'); } });
                    if (missing.length === 0) return null;
                    return (
                        <Alert severity='error' sx={{ mt: 1 }}>
                            <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                                Missing required fields:
                            </Typography>
                            {missing.map(m => (
                                <Box key={m.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                    <Typography variant='body2'>• {m.label}</Typography>
                                    <Button size='small' sx={{ textTransform: 'none', fontSize: '11px', minWidth: 0, p: '0 4px' }} onClick={m.action}>
                                        Fix →
                                    </Button>
                                </Box>
                            ))}
                        </Alert>
                    );
                })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUseAdvanedWarning(false)} color='secondary' variant='outlined'>
                        Cancel
                    </Button>
                    <Button
                        disabled={!account?.id || !transportationMethod || !incoterm || !products?.length || !routes?.length}
                        onClick={async () => {
                        setUseAdvanedWarning(false);
                        try {
                            // Save draft first to ensure opportunity exists on server
                            const saved = await onSaveDraft({ quiet: true });
                            if (!saved) {
                                alert('Please fix any errors and save the draft before switching to Form Builder.');
                                return;
                            }
                            await axios.put(`/api/opportunities/${id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                                opportunity: {
                                    type: 'one-off',
                                    offers: [],
                                    preface: getDefaultEmailBody({
                                        organization,
                                        transportationMethod: opportunity.transportationMethod,
                                        link: 'https://PLACEHOLDER_FOR_LINK'
                                    }),
                                    attributes: { ...opportunity?.attributes || {}, attachPDF: true },
                                }
                            });
                            window.location.href = `/organization/${organization.alias}/opportunities/${id}/`;
                        } catch (e) {
                            console.error('Failed to switch to form builder', e);
                            alert('Failed to switch to form builder: ' + (e.response?.data?.detail || e.message));
                        }
                    }} color='primary' variant='contained'>
                        Use Form Builder
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showDeleteConfirm}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>
                    Delete Draft
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this draft? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color='secondary' variant='outlined'>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color='error' variant='contained'>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default OpportunityEditor;